import { v4 as uuid } from 'uuid';
import { Flow, UsageProfile } from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { AtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '../store/useFloorplanDesignStore';
import { areOnlySingularStationAreas } from '@/modules/floorplanDesign/helpers/typeguards';

interface TransformFlowCreationParameters {
  name: string;
  sourceAreaOrFlowStopId: string;
  destinationAreaOrFlowStopId: string;
}

interface ConsolidationRetrievalFlowCreationParameters {
  name: string;
  sourceAreaOrFlowStopId: string;
  destinationAreaOrFlowStopId: string;
}

interface ConsolidationStorageFlowCreationParameters {
  name: string;
  sourceAreaOrFlowStopId: string;
  destinationAreaOrFlowStopId: string;
}

interface GoodsToPersonsFlowCreationParameters {
  name: string;
  sourceAreaOrFlowStopId: string;
  destinationAreaOrFlowStopId: string;
}

interface ExternalFlowCreationParameters {
  name: string;
  sourceAreaOrFlowStopId: string;
  destinationAreaOrFlowStopId: string;
}

type buildFlowParams =
  | {
      flowType: Extract<Flow['type'], 'TRANSPORT'>;
      creationParams: TransformFlowCreationParameters;
    }
  | {
      flowType: Extract<Flow['type'], 'CONSOLIDATION_STORAGE'>;
      creationParams: ConsolidationRetrievalFlowCreationParameters;
    }
  | {
      flowType: Extract<Flow['type'], 'CONSOLIDATION_RETRIEVAL'>;
      creationParams: ConsolidationStorageFlowCreationParameters;
    }
  | {
      flowType: Extract<Flow['type'], 'GOODS_TO_PERSONS'>;
      creationParams: GoodsToPersonsFlowCreationParameters;
    }
  | {
      flowType: Extract<Flow['type'], 'EXTERNAL'>;
      creationParams: ExternalFlowCreationParameters;
    };

interface IFlowFactory {
  buildFlow(buildAreaParams: buildFlowParams): Flow;
}

class FlowFactory implements IFlowFactory {
  /* Overloads to get type safety */
  buildFlow(params: {
    flowType: 'TRANSPORT';
    creationParams: TransformFlowCreationParameters;
  }): Flow;
  buildFlow(params: {
    flowType: 'CONSOLIDATION_STORAGE';
    creationParams: ConsolidationStorageFlowCreationParameters;
  }): Flow;
  buildFlow(params: {
    flowType: 'CONSOLIDATION_RETRIEVAL';
    creationParams: ConsolidationRetrievalFlowCreationParameters;
  }): Flow;
  buildFlow(params: {
    flowType: 'GOODS_TO_PERSONS';
    creationParams: GoodsToPersonsFlowCreationParameters;
  }): Flow;
  buildFlow(params: { flowType: 'EXTERNAL'; creationParams: ExternalFlowCreationParameters }): Flow;

  buildFlow(buildFlowParams: buildFlowParams) {
    const { flowType, creationParams } = buildFlowParams;
    switch (flowType) {
      case 'TRANSPORT':
        return this.buildTransportFlow(creationParams);
      case 'CONSOLIDATION_STORAGE':
        return this.buildConsolidationStorageFlow(creationParams);
      case 'CONSOLIDATION_RETRIEVAL':
        return this.buildConsolidationRetrievalFlow(creationParams);
      case 'GOODS_TO_PERSONS':
        return this.buildGoodsToPersonsFlow(creationParams);
      case 'EXTERNAL':
        return this.buildExternalFlow(creationParams);
      default:
        assertNever(flowType);
    }
  }

  private buildTransportFlow(params: TransformFlowCreationParameters): Flow {
    const { getPreference, getAreaOrAreasInFlowStopById } = useFloorplanDesignStore.getState();
    const { name, sourceAreaOrFlowStopId, destinationAreaOrFlowStopId } = params;

    // use source areas to determine if transportExtension should be set to undefined or the preference value
    const sourceAreas = getAreaOrAreasInFlowStopById(sourceAreaOrFlowStopId);
    const transportExtension =
      !sourceAreas ||
      !areOnlySingularStationAreas(sourceAreas) ||
      !sourceAreas.every((area) =>
        area.loadHandlingPoiSpecs.some((poiSpec) => poiSpec.canBeUsedForParking === true),
      )
        ? undefined
        : {
            preAnnounceDuration: getPreference('flow.transportExtension.preAnnounceDuration'),
            maxActivePreAnnouncedAvOrders: getPreference(
              'flow.transportExtension.maxActivePreAnnouncedAvOrders',
            ),
          };

    return {
      type: 'TRANSPORT',
      id: uuid(),
      name,
      sourceAreaOrFlowStopId,
      destinationAreaOrFlowStopId,
      vehicleLimit: getPreference('flow.vehicleLimit/transport'),
      hourlyOrderCounts: this.buildDefaultOrderWindow([
        getPreference('flow.hourlyOrderCounts/transport'),
      ]),
      transportExtension,
    };
  }

  private buildConsolidationStorageFlow(params: ConsolidationStorageFlowCreationParameters): Flow {
    const { getPreference, getAreaOrAreasInFlowStopById } = useFloorplanDesignStore.getState();
    const { name, sourceAreaOrFlowStopId, destinationAreaOrFlowStopId } = params;

    // use source areas to determine if preAnnounceDuration should be set to 0 or the preference value
    const sourceAreas = getAreaOrAreasInFlowStopById(sourceAreaOrFlowStopId);
    const preAnnounceDuration =
      !sourceAreas ||
      !areOnlySingularStationAreas(sourceAreas) ||
      !sourceAreas.every((area) =>
        area.loadHandlingPoiSpecs.some((poiSpec) => poiSpec.canBeUsedForParking === true),
      )
        ? 0
        : getPreference('flow.consolidationStorageExtension.preAnnounceDuration');

    return {
      type: 'CONSOLIDATION_STORAGE',
      id: uuid(),
      name,
      sourceAreaOrFlowStopId,
      destinationAreaOrFlowStopId,
      vehicleLimit: getPreference('flow.vehicleLimit/consolidationStorage'),
      hourlyOrderCounts: this.buildDefaultOrderWindow([
        getPreference('flow.hourlyOrderCounts/consolidationStorage'),
      ]),
      consolidationStorageExtension: {
        batchSize: getPreference('flow.consolidationStorageExtension.batchSize'),
        numConsolidations: getPreference('flow.consolidationStorageExtension.numConsolidations'),
        preAnnounceDuration,
        maxActivePreAnnouncedAvOrders: getPreference(
          'flow.consolidationStorageExtension.maxActivePreAnnouncedAvOrders',
        ),
      },
    };
  }

  private buildConsolidationRetrievalFlow(
    params: ConsolidationRetrievalFlowCreationParameters,
  ): Flow {
    const { getPreference } = useFloorplanDesignStore.getState();
    const { name, sourceAreaOrFlowStopId, destinationAreaOrFlowStopId } = params;

    return {
      type: 'CONSOLIDATION_RETRIEVAL',
      id: uuid(),
      name,
      sourceAreaOrFlowStopId,
      destinationAreaOrFlowStopId,
      vehicleLimit: getPreference('flow.vehicleLimit/consolidationRetrieval'),
      hourlyOrderCounts: this.buildDefaultOrderWindow([
        getPreference('flow.hourlyOrderCounts/consolidationRetrieval'),
      ]),
      consolidationRetrievalExtension: {
        shipmentSize: getPreference('flow.consolidationRetrievalExtension.shipmentSize'),
        numConsolidations: getPreference('flow.consolidationRetrievalExtension.numConsolidations'),
        retrievalStartDelayInHours: getPreference(
          'flow.consolidationRetrievalExtension.retrievalStartDelayInHours',
        ),
      },
    };
  }
  private buildGoodsToPersonsFlow(params: GoodsToPersonsFlowCreationParameters): Flow {
    const { name, sourceAreaOrFlowStopId, destinationAreaOrFlowStopId } = params;
    const { getPreference } = useFloorplanDesignStore.getState();
    return {
      type: 'GOODS_TO_PERSONS',
      id: uuid(),
      name,
      sourceAreaOrFlowStopId,
      destinationAreaOrFlowStopId,
      vehicleLimit: getPreference('flow.vehicleLimit/goodsToPersons'),
      hourlyOrderCounts: this.buildDefaultOrderWindow([
        getPreference('flow.hourlyOrderCounts/goodsToPersons'),
      ]),
      goodsToPersonsExtension: {
        aisleProfile: this.buildUsageProfile(
          getPreference('flow.goodsToPersonsExtension.aisleProfile.type'),
          getPreference('flow.goodsToPersonsExtension.aisleProfile.usageProfileLinear.startWeight'),
          getPreference('flow.goodsToPersonsExtension.aisleProfile.usageProfileLinear.endWeight'),
          getPreference('flow.goodsToPersonsExtension.aisleProfile.usageProfileExponential.rate'),
        ),
        shelfProfile: this.buildUsageProfile(
          getPreference('flow.goodsToPersonsExtension.shelfProfile.type'),
          getPreference('flow.goodsToPersonsExtension.shelfProfile.usageProfileLinear.startWeight'),
          getPreference('flow.goodsToPersonsExtension.shelfProfile.usageProfileLinear.endWeight'),
          getPreference('flow.goodsToPersonsExtension.shelfProfile.usageProfileExponential.rate'),
        ),
        depthProfile: this.buildUsageProfile(
          getPreference('flow.goodsToPersonsExtension.depthProfile.type'),
          getPreference('flow.goodsToPersonsExtension.depthProfile.usageProfileLinear.startWeight'),
          getPreference('flow.goodsToPersonsExtension.depthProfile.usageProfileLinear.endWeight'),
          getPreference('flow.goodsToPersonsExtension.depthProfile.usageProfileExponential.rate'),
        ),
        processAreaUsage: this.buildUsageProfile(
          getPreference('flow.goodsToPersonsExtension.processAreaUsage.type'),
          getPreference(
            'flow.goodsToPersonsExtension.processAreaUsage.usageProfileLinear.startWeight',
          ),
          getPreference(
            'flow.goodsToPersonsExtension.processAreaUsage.usageProfileLinear.endWeight',
          ),
          getPreference(
            'flow.goodsToPersonsExtension.processAreaUsage.usageProfileExponential.rate',
          ),
        ),
        sequencingHorizonWindow: getPreference(
          'flow.goodsToPersonsExtension.sequencingHorizonWindow',
        ),
        handoverCooldown: getPreference('flow.goodsToPersonsExtension.handoverCooldown'),
        processDuration: getPreference('flow.goodsToPersonsExtension.processDuration'),
        concurrentAisleCount: getPreference('flow.goodsToPersonsExtension.concurrentAisleCount'),
      },
    };
  }

  private buildExternalFlow(params: ExternalFlowCreationParameters): Flow {
    const { name, sourceAreaOrFlowStopId, destinationAreaOrFlowStopId } = params;
    const { getPreference, getRelatedFlows } = useFloorplanDesignStore.getState();
    const relatedFlows = getRelatedFlows([sourceAreaOrFlowStopId]);
    const hourlyOrderCount =
      relatedFlows.length > 0
        ? relatedFlows[0].hourlyOrderCounts[0]
        : getPreference('flow.hourlyOrderCounts/external');

    return {
      type: 'EXTERNAL',
      id: uuid(),
      name,
      sourceAreaOrFlowStopId,
      destinationAreaOrFlowStopId,
      vehicleLimit: getPreference('flow.vehicleLimit/external'),
      hourlyOrderCounts: this.buildDefaultOrderWindow([hourlyOrderCount]),
      externalFlowExtension: {
        processDuration: getPreference('flow.externalFlowsExtension.processDuration'),
      },
    };
  }

  private buildDefaultOrderWindow(
    defaultFlowHourlyOrders: [number, ...number[]],
  ): AtLeast1<number> {
    return [defaultFlowHourlyOrders[0], ...defaultFlowHourlyOrders.slice(1)];
  }

  private buildUsageProfile(
    type: UsageProfile['type'],
    startWeight: number,
    endWeight: number,
    rate: number,
  ): UsageProfile {
    switch (type) {
      case 'LINEAR':
        return {
          type,
          usageProfileLinear: {
            startWeight,
            endWeight,
          },
        };
      case 'EXPONENTIAL':
        return {
          type,
          usageProfileExponential: {
            rate,
          },
        };
      default:
        assertNever(type);
    }
  }
}

export const factory = new FlowFactory();

// eslint-disable-next-line no-underscore-dangle
export const __testOnly__FlowFactory = FlowFactory;
